import type { ParsedStatement, Transaction } from './types';
import { categorize } from './categorize';

/**
 * Capitec bank-statement parser.
 *
 * Statement layout (text-layer PDF, columns):
 *   Date (dd/mm/yyyy) | Description | Money In | Money Out | Balance
 *
 * Known quirks:
 *  - The "Money In" / "Money Out" column is sometimes empty when 0
 *  - The description may contain extra spaces; collapse them
 *  - The balance column is mandatory — we use balance arithmetic to validate
 *  - The opening balance appears on a separate "Opening Balance" line
 *  - The closing balance appears on a separate "Closing Balance" line
 */
const MONEY = '[-+]?\\d{1,3}(?:[\\s ]?\\d{3})*(?:[.,]\\d{2})?';
const DATE  = '(\\d{2}\\/\\d{2}\\/\\d{4})';

const ROW_RE = new RegExp(
  `^\\s*${DATE}\\s+(.+?)\\s+(${MONEY})?\\s+(${MONEY})?\\s+(${MONEY})\\s*$`,
);

// Some Capitec PDFs put the columns in a different order or with extra spacing.
// This more permissive fallback matches: date + free description + 1-3 numbers.
const ROW_RE_PERMISSIVE = new RegExp(
  `^\\s*${DATE}\\s{2,}(.+?)\\s+(${MONEY}(?:\\s+${MONEY}){1,2})\\s*$`,
);

const OPENING_RE = /Opening\s+Balance\s+([\d\s.,\-]+)/i;
const CLOSING_RE = /Closing\s+Balance\s+([\d\s.,\-]+)/i;

function parseMoney(raw: string | undefined): number | null {
  if (!raw) return null;
  // Normalise "1 234,56" or "1,234.56" → "1234.56"
  let s = raw.replace(/\s/g, '').replace(/[ ]/g, '');
  if (s.includes(',') && s.includes('.')) {
    // assume comma is thousands, dot is decimal
    s = s.replace(/,/g, '');
  } else if (s.includes(',')) {
    // single comma → treat as decimal
    s = s.replace(/,/g, '.');
  }
  const n = Number(s);
  return isFinite(n) ? n : null;
}

function normaliseDate(s: string): string {
  // dd/mm/yyyy → yyyy-mm-dd
  const [d, m, y] = s.split('/');
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

interface RawRow { date: string; description: string; moneyIn: number | null; moneyOut: number | null; balance: number; }

function tryParseRow(line: string): RawRow | null {
  // Strict first
  let m = ROW_RE.exec(line);
  if (m) {
    const [, date, desc, inStr, outStr, balStr] = m;
    return {
      date: normaliseDate(date),
      description: desc.trim().replace(/\s+/g, ' '),
      moneyIn: parseMoney(inStr),
      moneyOut: parseMoney(outStr),
      balance: parseMoney(balStr) ?? 0,
    };
  }
  // Permissive fallback: 1-3 numbers at the end
  m = ROW_RE_PERMISSIVE.exec(line);
  if (m) {
    const [, date, desc, nums] = m;
    const parts = nums.trim().split(/\s+/).map(parseMoney);
    // Last number is balance. Preceding are In / Out (any order; pick based on sign)
    const balance = parts[parts.length - 1] ?? 0;
    const ins = parts.slice(0, -1);
    let moneyIn: number | null = null;
    let moneyOut: number | null = null;
    if (ins.length >= 2) {
      moneyIn = ins[0];
      moneyOut = ins[1];
    } else if (ins.length === 1) {
      if ((ins[0] ?? 0) < 0) moneyOut = ins[0];
      else moneyIn = ins[0];
    }
    return {
      date: normaliseDate(date),
      description: desc.trim().replace(/\s+/g, ' '),
      moneyIn,
      moneyOut,
      balance,
    };
  }
  return null;
}

export function parseCapitec(text: string): ParsedStatement {
  const lines = text.split(/\r?\n/);
  const warnings: string[] = [];
  const rawRows: RawRow[] = [];
  let opening: number | null = null;
  let closing: number | null = null;

  for (const line of lines) {
    if (!line.trim()) continue;
    if (opening === null) {
      const om = OPENING_RE.exec(line);
      if (om) opening = parseMoney(om[1]);
    }
    if (closing === null) {
      const cm = CLOSING_RE.exec(line);
      if (cm) closing = parseMoney(cm[1]);
    }
    const row = tryParseRow(line);
    if (row) rawRows.push(row);
  }

  // Build transactions and validate balance arithmetic.
  // The parser is column-ambiguous (Money In vs Money Out can be empty),
  // so we derive the SIGN from balance arithmetic when columns are unclear.
  const transactions: Transaction[] = [];
  const balanceMismatches: number[] = [];

  let prevBalance: number | null = opening;
  rawRows.forEach((r, idx) => {
    if (r.moneyIn === null && r.moneyOut === null) {
      warnings.push(`Row ${idx + 1}: no amount parsed — "${r.description}"`);
      return;
    }

    let signed: number;
    if (r.moneyIn !== null && r.moneyOut !== null) {
      // Both columns have values: trust them
      signed = r.moneyIn - r.moneyOut;
    } else if (r.moneyIn !== null) {
      // Only Money In column populated
      signed = r.moneyIn;
    } else {
      // Only Money Out column populated
      signed = -(r.moneyOut as number);
    }

    // Derive sign from balance if available — if our computed sign disagrees
    // with what the balance change implies, flip it.
    if (prevBalance !== null) {
      const delta = Math.round((r.balance - prevBalance) * 100) / 100;
      const ourSign = signed < 0 ? -1 : (signed > 0 ? 1 : 0);
      const deltaSign = delta < 0 ? -1 : (delta > 0 ? 1 : 0);
      if (ourSign !== 0 && deltaSign !== 0 && ourSign !== deltaSign) {
        signed = -signed;
      }
    }

    if (prevBalance !== null) {
      const expected = Math.round((prevBalance + signed) * 100) / 100;
      const actual   = Math.round(r.balance * 100) / 100;
      if (Math.abs(expected - actual) > 0.01) {
        balanceMismatches.push(transactions.length);
        warnings.push(`Balance mismatch at row ${idx + 1}: expected ${expected.toFixed(2)}, got ${actual.toFixed(2)}`);
      }
    }
    prevBalance = r.balance;

    transactions.push({
      date: r.date,
      description: r.description,
      amount: Math.round(signed * 100) / 100,
      balance: r.balance,
      category: categorize(r.description),
    });
  });

  if (opening !== null && closing !== null) {
    const sum = transactions.reduce((s, t) => s + t.amount, 0);
    const expected = Math.round((opening + sum) * 100) / 100;
    if (Math.abs(expected - closing) > 0.01) {
      warnings.push(`Closing balance mismatch: opening ${opening} + sum ${sum.toFixed(2)} = ${expected.toFixed(2)}, statement says ${closing}`);
    }
  }

  return {
    bank: 'capitec',
    account: null,
    openingBalance: opening,
    closingBalance: closing,
    transactions,
    warnings,
    balanceMismatches,
  };
}
